import { useState, type FormEvent, type ReactNode } from "react";
import { describeSchedule, nextRuns, scheduleProblem, type WatchSchedule } from "../../shared/cron";
import type { ComputerWatchView, WatchesView, WatchItemView, WatchReplayStatus, WatchSource, WatchView } from "../../shared/watches";
import { Button, cx, ErrorNotice, Field, Input, Loading, Notice, PageTop, Select } from "../components/ui";
import { useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { fmtDateTime, fmtRelative } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { useResource } from "../lib/store";

type Preset = "adaptive" | "1h" | "3h" | "6h" | "daily" | "everyHours" | "cron";

const PRESETS: { value: Preset; label: string }[] = [
  { value: "adaptive", label: "Adaptatif" },
  { value: "1h", label: "Toutes les heures" },
  { value: "3h", label: "Toutes les 3 h" },
  { value: "6h", label: "Toutes les 6 h" },
  { value: "daily", label: "Chaque jour à…" },
  { value: "everyHours", label: "Toutes les X heures" },
  { value: "cron", label: "Cron personnalisé" },
];

const SOURCE_LABEL: Record<WatchSource, string> = { web: "Web", gmail: "Gmail", slack: "Slack", computer: "Ordinateur" };

/** How the last run of a computer watch read its site. */
const REPLAY: Record<WatchReplayStatus, { label: string; tone: "success" | "accent" | "neutral" | "warning" | "danger" }> = {
  learned: { label: "script enregistré", tone: "accent" },
  replayed: { label: "rejoué sans IA", tone: "success" },
  repaired: { label: "réparé", tone: "warning" },
  model: { label: "lu avec l'IA", tone: "neutral" },
  failed: { label: "échec", tone: "danger" },
};
/** A dot of color before the replay state: the only color of the block. */
const DOT: Record<(typeof REPLAY)[WatchReplayStatus]["tone"], string> = {
  success: "bg-success",
  accent: "bg-accent",
  neutral: "bg-subtle",
  warning: "bg-warning",
  danger: "bg-danger",
};

const OUTCOME: Record<WatchItemView["outcome"], { label: string; tone: "success" | "accent" | "neutral" | "warning" }> = {
  sent: { label: "Envoyé", tone: "success" },
  digest: { label: "Digest", tone: "accent" },
  dropped: { label: "Écarté", tone: "neutral" },
  noise: { label: "Bruit", tone: "neutral" },
  pending: { label: "En cours", tone: "warning" },
};

/** A 13 px faint text button (« Changer la fréquence », « Supprimer »). */
const QUIET = "inline-flex min-h-11 items-center text-[13px] text-subtle transition hover:text-fg sm:min-h-8";
/** An inset well inside a card (the computer's block, the edit forms). */
const WELL = "rounded-2xl bg-bg p-3.5";

interface ScheduleForm {
  preset: Preset;
  time: string;
  hours: string;
  cron: string;
}

function presetOf(s: WatchSchedule): ScheduleForm {
  const base: ScheduleForm = { preset: "adaptive", time: "08:00", hours: "4", cron: "0 8 * * 1-5" };
  switch (s.kind) {
    case "adaptive":
      return base;
    case "every":
      if (s.minutes === 60) return { ...base, preset: "1h" };
      if (s.minutes === 180) return { ...base, preset: "3h" };
      if (s.minutes === 360) return { ...base, preset: "6h" };
      return { ...base, preset: "everyHours", hours: String(Math.max(1, Math.round(s.minutes / 60))) };
    case "daily":
      return { ...base, preset: "daily", time: s.time };
    case "cron":
      return { ...base, preset: "cron", cron: s.expr };
  }
}

/** The schedule a form describes, or a French error. */
function scheduleOf(f: ScheduleForm): WatchSchedule | string {
  let s: WatchSchedule;
  switch (f.preset) {
    case "adaptive":
      return { kind: "adaptive" };
    case "1h":
      return { kind: "every", minutes: 60 };
    case "3h":
      return { kind: "every", minutes: 180 };
    case "6h":
      return { kind: "every", minutes: 360 };
    case "daily":
      s = { kind: "daily", time: f.time };
      break;
    case "everyHours": {
      const n = Number(f.hours.replace(",", "."));
      if (!Number.isFinite(n) || n <= 0) return "Indique un nombre d'heures.";
      s = { kind: "every", minutes: Math.round(n * 60) };
      break;
    }
    case "cron":
      s = { kind: "cron", expr: f.cron.trim() };
      break;
  }
  return scheduleProblem(s) ?? s;
}

/** Fields of a schedule with a human preview (description and the next runs). */
function ScheduleFields({ id, value, onChange, timezone }: { id: string; value: ScheduleForm; onChange: (v: ScheduleForm) => void; timezone: string }) {
  const now = useNow(60_000);
  const parsed = scheduleOf(value);
  const preview = typeof parsed === "string" ? null : parsed;
  const runs = preview ? nextRuns(preview, timezone, now, 3) : [];
  return (
    <div className="space-y-3">
      <Field label="Fréquence" htmlFor={`${id}-preset`}>
        <Select id={`${id}-preset`} value={value.preset} onChange={(e) => onChange({ ...value, preset: e.target.value as Preset })}>
          {PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
      </Field>
      {value.preset === "daily" ? (
        <Field label="Heure" htmlFor={`${id}-time`}>
          <Input id={`${id}-time`} type="time" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} />
        </Field>
      ) : null}
      {value.preset === "everyHours" ? (
        <Field label="Toutes les … heures" htmlFor={`${id}-hours`}>
          <Input id={`${id}-hours`} inputMode="decimal" value={value.hours} onChange={(e) => onChange({ ...value, hours: e.target.value })} />
        </Field>
      ) : null}
      {value.preset === "cron" ? (
        <Field label="Expression cron" htmlFor={`${id}-cron`} hint="minute heure jour mois jour-de-la-semaine, à ton heure locale (ex. 0 8 * * 1-5)">
          <Input id={`${id}-cron`} value={value.cron} spellCheck={false} onChange={(e) => onChange({ ...value, cron: e.target.value })} />
        </Field>
      ) : null}
      <p className="text-[12.5px] text-subtle" data-testid="schedule-preview" aria-live="polite">
        {typeof parsed === "string" ? (
          <span className="text-danger">{parsed}</span>
        ) : (
          <>
            {describeSchedule(parsed).replace(/^./, (c) => c.toLocaleUpperCase("fr"))}
            {preview?.kind === "adaptive" ? " : plus souvent quand il y a du nouveau, moins quand c'est calme." : runs.length ? ` — prochains passages : ${runs.map((r) => fmtDateTime(r, now)).join(", ")}` : ""}
          </>
        )}
      </p>
    </div>
  );
}

function ItemRow({ item, onFeedback, busy }: { item: WatchItemView; onFeedback: (more: boolean) => void; busy: boolean }) {
  const o = OUTCOME[item.outcome];
  return (
    <li className="flex flex-col gap-1 py-2.5 text-[14.5px]" data-testid="watch-item">
      <div className="flex min-w-0 items-baseline justify-between gap-2">
        {item.url ? (
          <a href={item.url} target="_blank" rel="noreferrer noopener" className="min-w-0 truncate hover:underline">
            {item.title}
          </a>
        ) : (
          <span className="min-w-0 truncate">{item.title}</span>
        )}
        <span className={cx("shrink-0 text-[12.5px]", o.tone === "success" ? "text-success-ink" : o.tone === "accent" ? "text-accent" : "text-subtle")}>{o.label}</span>
      </div>
      {item.reason ? <p className="text-[12.5px] text-subtle">{item.reason}</p> : null}
      <div className="mt-0.5 flex gap-1.5">
        <FeedbackChip pressed={item.feedback === "more"} disabled={busy} onClick={() => onFeedback(true)}>
          Plus de ça
        </FeedbackChip>
        <FeedbackChip pressed={item.feedback === "less"} disabled={busy} onClick={() => onFeedback(false)}>
          Moins de ça
        </FeedbackChip>
      </div>
    </li>
  );
}

function FeedbackChip({ pressed, disabled, onClick, children }: { pressed: boolean; disabled: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "h-11 rounded-full px-3 text-[13px] transition disabled:opacity-50 sm:h-8",
        pressed ? "bg-ink font-semibold text-ink-fg" : "text-fg shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-chip",
      )}
    >
      {children}
    </button>
  );
}

/** A computer watch: its run in progress (live link), how its last run read the site, its last snapshot. */
function ComputerDetails({ computer }: { computer: ComputerWatchView }) {
  const now = useNow(30_000);
  const snap = computer.snapshot;
  return (
    <div className={cx(WELL, "flex flex-col gap-2.5")} data-testid="watch-computer">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        {computer.running ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-fg">
              <span aria-hidden="true" className="live-dot h-[7px] w-[7px] rounded-full bg-accent" />
              {computer.running.kind === "replay" ? "Relevé en cours (sans IA)" : "Relevé en cours"}
            </span>
            <a href={computer.running.link} target="_blank" rel="noreferrer noopener" className="font-semibold text-fg underline underline-offset-[3px] hover:text-muted" data-testid="watch-live">
              Voir en direct
            </a>
          </>
        ) : null}
        {computer.replay ? (
          <span className="inline-flex items-center gap-1.5 text-muted">
            <span aria-hidden="true" className={cx("h-[7px] w-[7px] rounded-full", DOT[REPLAY[computer.replay].tone])} />
            <span data-testid="watch-replay">{REPLAY[computer.replay].label}</span>
          </span>
        ) : (
          <span className="text-muted">Premier relevé à venir</span>
        )}
        <span className="text-subtle">au plus {computer.minIntervalMinutes === 60 ? "une fois par heure" : `toutes les ${computer.minIntervalMinutes} min`}</span>
      </div>
      {snap ? (
        <div>
          <h3 className="text-[12.5px] font-semibold text-subtle">Dernier relevé · {fmtDateTime(snap.at, now)}</h3>
          {snap.summary ? <p className="mt-1 text-[14px] leading-normal text-body">{snap.summary}</p> : null}
          <dl className="mt-1.5 grid grid-cols-1 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2" data-testid="watch-facts">
            {snap.facts.map((f) => (
              <div key={f.key} className="min-w-0">
                <dt className="text-subtle">{f.key}</dt>
                <dd className="wrap-anywhere">{f.value}</dd>
              </div>
            ))}
          </dl>
          {snap.suggestions.length ? (
            <p className="mt-2 text-[13px]">
              <span className="text-subtle">Suggestions (rien n'est fait sans toi) : </span>
              {snap.suggestions.join(" · ")}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function WatchCard({ watch, timezone, onChange }: { watch: WatchView; timezone: string; onChange: (view: WatchesView) => void }) {
  const now = useNow(60_000);
  const [editing, setEditing] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goal, setGoal] = useState(watch.query);
  const [form, setForm] = useState<ScheduleForm>(() => presetOf(watch.schedule));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = watch.status === "active";

  async function act(fn: () => Promise<WatchesView>) {
    setBusy(true);
    setError(null);
    try {
      onChange(await fn());
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function saveSchedule(e: FormEvent) {
    e.preventDefault();
    const schedule = scheduleOf(form);
    if (typeof schedule === "string") return setError(schedule);
    if (await act(() => api("PATCH /api/watches/:id", { params: { id: watch.id }, body: { schedule } }))) setEditing(false);
  }

  async function saveGoal(e: FormEvent) {
    e.preventDefault();
    if (await act(() => api("PATCH /api/watches/:id", { params: { id: watch.id }, body: { query: goal } }))) setEditingGoal(false);
  }

  // Only what the watch actually kept is shown: items the triage judged noise or the judge dropped stay out of sight
  // (they still teach the learner; the last run line counts them).
  const shown = watch.items.filter((item) => item.outcome !== "noise" && item.outcome !== "dropped");
  return (
    <article
      data-testid="watch-card"
      className={cx("flex min-w-0 flex-col gap-3 rounded-[20px] p-4 sm:p-[18px]", active ? "bg-elev" : "bg-bg shadow-[inset_0_0_0_1px_var(--border)]")}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className={cx("text-[16px] font-semibold wrap-anywhere", !active && "text-muted")}>{watch.label}</h2>
          <p className="mt-0.5 text-[12.5px] text-subtle" title={`« ${watch.query} »`}>
            {!active ? <span className="font-semibold text-muted">En pause</span> : null}
            {!active ? " · " : null}
            {SOURCE_LABEL[watch.source]} · <span data-testid="watch-schedule">{watch.scheduleText}</span>
            {watch.lastRunAt ? ` · vu ${fmtRelative(watch.lastRunAt, now)}` : ""}
          </p>
        </div>
        <button
          type="button"
          aria-pressed={active}
          aria-label={active ? "Mettre en pause" : "Reprendre"}
          disabled={busy}
          onClick={() => void act(() => api("PATCH /api/watches/:id", { params: { id: watch.id }, body: { status: active ? "paused" : "active" } }))}
          className="-my-2 flex h-11 shrink-0 items-center disabled:opacity-50"
        >
          <span className={cx("relative block h-7 w-[46px] rounded-full transition-colors", active ? "bg-accent" : "bg-[var(--track)]")}>
            <span className={cx("absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white transition-all", active ? "left-[21px]" : "left-[3px]")} />
          </span>
        </button>
      </div>
      {watch.source === "computer" ? (
        <p className="text-[14px] leading-normal text-body wrap-anywhere" data-testid="watch-goal">
          « {watch.query} »
        </p>
      ) : null}
      {watch.lastResult ? <p className="text-[14.5px] leading-normal text-body">{watch.lastResult}</p> : null}
      {shown.length ? (
        <ul className="flex flex-col divide-y divide-line">
          {shown.map((item) => (
            <ItemRow key={item.id} item={item} busy={busy} onFeedback={(more) => void act(() => api("POST /api/watches/items/:id/feedback", { params: { id: item.id }, body: { more } }))} />
          ))}
        </ul>
      ) : null}
      {watch.computer ? <ComputerDetails computer={watch.computer} /> : null}
      {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}
      {editingGoal ? (
        <form onSubmit={saveGoal} className={cx(WELL, "space-y-3")}>
          <Field
            label={watch.source === "computer" ? "Objectif du relevé" : "Sujet à surveiller"}
            htmlFor={`w-${watch.id}-goal`}
            hint={watch.source === "computer" ? "Un nouvel objectif est appris à nouveau au prochain passage (avec l'IA, puis rejoué sans)." : undefined}
          >
            <Input id={`w-${watch.id}-goal`} value={goal} onChange={(e) => setGoal(e.target.value)} required minLength={2} maxLength={300} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Enregistrer
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setEditingGoal(false)}>
              Annuler
            </Button>
          </div>
        </form>
      ) : null}
      {editing ? (
        <form onSubmit={saveSchedule} className={cx(WELL, "space-y-3")}>
          <ScheduleFields id={`w-${watch.id}`} value={form} onChange={setForm} timezone={timezone} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              Enregistrer
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Annuler
            </Button>
          </div>
        </form>
      ) : editingGoal ? null : (
        <div className="-mb-1 flex flex-wrap gap-x-4">
          <button type="button" onClick={() => setEditing(true)} className={QUIET}>
            Changer la fréquence
          </button>
          <button
            type="button"
            onClick={() => {
              setGoal(watch.query);
              setEditingGoal(true);
            }}
            className={QUIET}
          >
            {watch.source === "computer" ? "Modifier l'objectif" : "Modifier le sujet"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Supprimer la veille « ${watch.label} » ?`)) void act(() => api("DELETE /api/watches/:id", { params: { id: watch.id } }));
            }}
            className={QUIET}
          >
            Supprimer
          </button>
        </div>
      )}
    </article>
  );
}

const QUICK: { preset: Preset | "other"; label: (agent: string) => string; form?: Partial<ScheduleForm> }[] = [
  { preset: "adaptive", label: (agent) => `${agent} choisit` },
  { preset: "3h", label: () => "Toutes les 3 h" },
  { preset: "daily", label: () => "Chaque jour à 9 h", form: { time: "09:00" } },
  { preset: "other", label: () => "Autre…" },
];

/** « Que dois-je surveiller ? »: one sentence, a rhythm, and « Surveiller ». */
function CreateForm({ timezone, agentName, onCreated }: { timezone: string; agentName: string; onCreated: (v: WatchesView) => void }) {
  const [query, setQuery] = useState("");
  const [label, setLabel] = useState("");
  const [source, setSource] = useState<WatchSource>("web");
  const [quick, setQuick] = useState<Preset | "other">("adaptive");
  const [schedule, setSchedule] = useState<ScheduleForm>(() => presetOf({ kind: "adaptive" }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const s = scheduleOf(schedule);
    if (typeof s === "string") return setError(s);
    setBusy(true);
    setError(null);
    try {
      onCreated(await api("POST /api/watches", { body: { query, ...(label.trim() ? { label: label.trim() } : {}), source, schedule: s } }));
      setQuery("");
      setLabel("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-[20px] bg-elev p-4 sm:p-[18px]">
      <label htmlFor="watch-query" className="px-0.5 text-[13px] font-semibold text-subtle">
        Que dois-je surveiller ?
      </label>
      <div className="flex gap-2 max-sm:flex-col">
        <input
          id="watch-query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          required
          minLength={2}
          maxLength={300}
          placeholder={source === "computer" ? "Ex. mon colis La Poste" : "Ex. un T3 à Lyon sous 1 100 €"}
          className="h-12 min-w-0 shrink-0 rounded-full bg-bg sm:flex-1 px-5 text-[15.5px] text-fg shadow-[inset_0_0_0_1px_var(--border)] outline-none placeholder:text-subtle focus:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)]"
        />
        <Button type="submit" busy={busy} className="h-12 px-5 text-[15px]">
          Surveiller
        </Button>
      </div>
      <div role="group" aria-label="Rythme" className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[13px] text-subtle">Rythme</span>
        {QUICK.map((q) => {
          const on = quick === q.preset;
          return (
            <button
              key={q.preset}
              type="button"
              aria-pressed={on}
              onClick={() => {
                setQuick(q.preset);
                if (q.preset !== "other") setSchedule({ ...presetOf({ kind: "adaptive" }), preset: q.preset, ...q.form });
              }}
              className={cx(
                "h-11 rounded-full px-3 text-[13.5px] transition sm:h-[34px]",
                on ? "bg-ink font-semibold text-ink-fg" : "text-fg shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-chip",
              )}
            >
              {q.label(agentName)}
            </button>
          );
        })}
      </div>
      {quick === "other" ? (
        <div className={cx(WELL, "space-y-3")}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nom (facultatif)" htmlFor="watch-label">
              <Input id="watch-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
            </Field>
            <Field label="Où chercher" htmlFor="watch-source">
              <Select id="watch-source" value={source} onChange={(e) => setSource(e.target.value as WatchSource)}>
                <option value="web">Web</option>
                <option value="gmail">Gmail</option>
                <option value="slack">Slack</option>
                <option value="computer">Ordinateur (sites, connexion, formulaires)</option>
              </Select>
            </Field>
          </div>
          {source === "computer" ? (
            <p className="text-[12.5px] text-subtle">Il regarde, sans jamais commander, réserver ni payer.</p>
          ) : null}
          <ScheduleFields id="watch-new" value={schedule} onChange={setSchedule} timezone={timezone} />
        </div>
      ) : null}
      {error ? <Notice tone="danger" role="alert">{error}</Notice> : null}
    </form>
  );
}

/** /veilles — what the agent watches on its own, how often, and what became of each thing it found. */
export function WatchesPage() {
  usePageTitle("Veilles");
  const settings = useSettings();
  // While a computer watch runs, the page follows it (its live link, then its new snapshot).
  const watches = useResource<WatchesView>("watches", async (signal) => api("GET /api/watches", { signal }), {
    pollMs: (data) => (data?.watches.some((w) => w.computer?.running) ? 5_000 : false),
  });
  const agentName = settings.data?.agentName ?? "Ton agent";
  const timezone = settings.data?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const set = (v: WatchesView) => watches.mutate(() => v);
  const list = watches.data?.watches ?? [];
  return (
    <div className="mx-auto flex w-full max-w-[620px] flex-col gap-[18px]">
      <PageTop title="Veilles" className="mb-0" />
      <CreateForm timezone={timezone} agentName={agentName} onCreated={set} />
      {watches.error ? <ErrorNotice message={watches.error.message} onRetry={() => void watches.reload()} /> : null}
      {watches.loading ? (
        <Loading />
      ) : list.length ? (
        <div className="flex flex-col gap-3">
          {list.map((w) => (
            <WatchCard key={`${w.id}:${JSON.stringify(w.schedule)}`} watch={w} timezone={timezone} onChange={set} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
